import React, { useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '@dotcom/crown-ui';
import styles from './low-resolution-tooltip.module.scss';

type LowResolutionTooltipTriggerProps = {
  isOpen: boolean;
  position?: { x: number; y: number };
};

// Breathing room between the tooltip's right edge and the viewport boundary.
// Prevents the tooltip from sitting flush against the screen edge on mobile.
const OVERFLOW_SAFE_MARGIN_PX = 8;

export const LowResolutionTooltipTrigger = ({
  isOpen,
  position = { x: 0, y: 0 },
}: LowResolutionTooltipTriggerProps): React.ReactElement | null => {
  const { t } = useTranslation();
  const anchorRef = useRef<HTMLDivElement>(null);

  // useLayoutEffect runs synchronously after DOM mutations but before the browser paints,
  // so the correction is applied before the user sees the tooltip. Using useEffect instead
  // would cause a visible flicker as the tooltip first renders clipped, then shifts.
  //
  // CUI Tooltip does not expose a placement prop, so we measure the rendered popup's
  // position ourselves and shift the anchor left if it overflows the right viewport edge.
  // This is needed on mobile when the warning icon sits near the right side of the card.
  useLayoutEffect(() => {
    if (!anchorRef.current) return;
    anchorRef.current.style.transform = '';
    if (!isOpen) return;

    // Query the CUI tooltip popup element by its CSS module class pattern, since the
    // class name is hashed and not predictable at build time.
    const popup = anchorRef.current.querySelector<HTMLElement>('[class*="tooltip-module_tooltip__"]');
    if (!popup || popup.getBoundingClientRect().width === 0) return;

    const overflow = popup.getBoundingClientRect().right - window.innerWidth + OVERFLOW_SAFE_MARGIN_PX;
    if (overflow > 0) {
      anchorRef.current.style.transform = `translateX(-${overflow}px)`;
    }
  }, [isOpen, position]);

  return (
    <div
      ref={anchorRef}
      className={styles['tooltip-anchor']}
      style={{ left: position.x, top: position.y }}
      data-testid="low-resolution-tooltip"
    >
      <Tooltip
        isOpen={isOpen}
        title={t('lowResolutionImage.tooltip.title') as string}
        triggerElement={<span className={styles['tooltip-trigger']} />}
        testId="low-resolution-cui-tooltip"
      >
        {t('lowResolutionImage.tooltip.message')}
      </Tooltip>
    </div>
  );
};
